import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { LabeledTextField, LabeledSelect } from '@/__core/components/LabeledField';
import MessageSnackbar from '@/__core/helpers/components/MessageSnackbar';
import { formatErrorForSnackbar } from '@/__core/helpers/Error';
import { createWalletThunk } from '@/modules/settings/wallet/redux/WalletRedux';
import { useDispatch, useSelector } from 'react-redux';
import { selectCompanies, fetchCompanies, selectCompanyLoading } from '@/modules/organizations/redux/CompanyRedux';
import { CreateWalletRequest } from '@/modules/finance/models/Wallet';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import { Cancel as CancelIcon, Save as SaveIcon } from '@mui/icons-material';

interface WalletDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (walletData: { id: string; name: string; code: string }) => Promise<void>;
  loading?: boolean;
}

export default function WalletDialog({
  open,
  onClose,
  onSave,
  loading = false,
}: WalletDialogProps) {
  const dispatch = useDispatch();
  const companies = useSelector(selectCompanies);
  const companyLoading = useSelector(selectCompanyLoading);
  const selectedCompany = useSelector((state: any) => state.auth.selectedCompany);
  const fetchedCompaniesThisOpenRef = React.useRef(false);
  
  // Check if user has global role - check both globalRoles and selectedCompany roles
  const globalRoles = useSelector((state: any) => state.auth.globalRoles);
  const hasGlobalRole = 
    (globalRoles && globalRoles.length > 0) ||
    selectedCompany?.roles?.some((role: any) => 
      role.role_name === 'global' || 
      role.role_level === 'global' || 
      role.role_name === 'global_admin'
    ) ||
    selectedCompany?.company_type === 'global' ||
    false;
  
  const [formData, setFormData] = useState<CreateWalletRequest>({
    name: '',
    code: '',
    description: '',
    order_number: 0,
    is_active: true,
    company_id: '',
  });
  const [error, setError] = useState<string | undefined>(undefined);
  const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);

  // Reset one-time fetch guard when dialog closes.
  React.useEffect(() => {
    if (!open) {
      fetchedCompaniesThisOpenRef.current = false;
    }
  }, [open]);

  // Fetch companies one time per dialog-open cycle for global users.
  React.useEffect(() => {
    if (!open || !hasGlobalRole) {
      return;
    }

    if (fetchedCompaniesThisOpenRef.current) {
      return;
    }

    if (companyLoading || companies.length > 0) {
      fetchedCompaniesThisOpenRef.current = true;
      return;
    }

    fetchedCompaniesThisOpenRef.current = true;

    if (!companyLoading && companies.length === 0) {
      dispatch(fetchCompanies());
    }
  }, [open, hasGlobalRole, dispatch, companyLoading, companies.length]);

  // Set company_id from storage if not global role
  React.useEffect(() => {
    if (open && !hasGlobalRole) {
      // Get company from Redux store first, fallback to storage
      const companyId = selectedCompany?.company_id;
      if (companyId) {
        setFormData(prev => ({
          ...prev,
          company_id: companyId,
        }));
      }
    } else if (open && hasGlobalRole) {
      // Reset company_id when dialog opens for global users
      setFormData(prev => ({
        ...prev,
        company_id: '',
      }));
    }
  }, [open, hasGlobalRole, selectedCompany?.company_id]);

  const handleInputChange = (field: keyof CreateWalletRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Tên ví không được để trống');
      return;
    }

    if (!formData.code.trim()) {
      setError('Mã ví không được để trống');
      return;
    }

    if (!formData.company_id) {
      setError('Công ty không được để trống');
      return;
    }

    try {
      setError(undefined);
      const result = await dispatch(createWalletThunk(formData));
      
      if (createWalletThunk.fulfilled.match(result)) {
        const newWallet = result.payload;
        await onSave({
          id: newWallet.id,
          name: newWallet.name,
          code: newWallet.code,
        });
        
        setSuccessMessage('Tạo ví thành công!');
        setFormData({
          name: '',
          code: '',
          description: '',
          order_number: 0,
          is_active: true,
          company_id: '',
        });
        
        // Close dialog after a short delay
        setTimeout(() => {
          onClose();
          setSuccessMessage(undefined);
        }, 1500);
      } else if (createWalletThunk.rejected.match(result)) {
        const errorMessage = result.error?.message || 'Có lỗi xảy ra khi tạo ví';
        setError(formatErrorForSnackbar(new Error(errorMessage)) || 'Có lỗi xảy ra khi tạo ví');
      }
      
    } catch (error: any) {
      setError(formatErrorForSnackbar(error) || 'Có lỗi xảy ra khi tạo ví');
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      order_number: 0,
      is_active: true,
      company_id: '',
    });
    setError(undefined);
    setSuccessMessage(undefined);
    onClose();
  };

  const disabled = !formData.name.trim() || !formData.code.trim() || !formData.company_id || loading;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Tạo ví mới</DialogTitle>
        <DialogContent>
          <LabeledTextField
            label="Tên tài khoản"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            size="small"
            required
            error={!!error && error.includes('Tên ví')}
            helperText={error && error.includes('Tên ví') ? error : ''}
          />
          
          <LabeledTextField
            label="Mã tài khoản"
            value={formData.code}
            onChange={(e) => handleInputChange('code', e.target.value)}
            placeholder="Nhập mã..."
            size="small"
            required
            error={!!error && error.includes('Mã ví')}
            helperText={error && error.includes('Mã ví') ? error : ''}
          />

          <LabeledTextField
            label="Mô tả"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Nhập mô tả (tùy chọn)..."
            size="small"
          />

          {hasGlobalRole && (
            <LabeledSelect
              label="Công ty"
              value={formData.company_id}
              onChange={(e) => handleInputChange('company_id', e.target.value)}
              size="small"
              required
              error={!!error && error.includes('Công ty')}
              helperText={error && error.includes('Công ty') ? error : ''}
              options={[
                { id: '', name: 'Chọn công ty' },
                ...companies.map((company) => ({
                  id: company.id,
                  name: company.name,
                }))
              ]}
            >
              <option value="">Chọn công ty</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </LabeledSelect>
          )}

          {error && !error.includes('Tên ví') && !error.includes('Mã ví') && !error.includes('Công ty') && (
            <MessageSnackbar
              open={true}
              message={error}
              severity="error"
              onClose={() => setError(undefined)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={loading}
            variant="contained"
            size="small"
            startIcon={<CancelIcon />}
            sx={sharedFormStyles.cancelButton}
          >
            Hủy
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="small"
            disabled={disabled}
            startIcon={<SaveIcon />}
            sx={sharedFormStyles.primaryButton}
          >
            {loading ? 'Đang tạo...' : 'Tạo ví'}
          </Button>
        </DialogActions>
      </Dialog>

      {successMessage && (
        <MessageSnackbar
          open={true}
          message={successMessage}
          severity="success"
          onClose={() => setSuccessMessage(undefined)}
        />
      )}
    </>
  );
}

